import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { LoadingSpinerArea, SetupMenu, SetupMenuItem } from '../../../../../components';
import { selectorUser } from '../../../../../lib/selectorUser';
import History from '../../../../../utils/History';
import { SetupCampaign } from '../SetupCampaign';
import { SetupFarma } from '../SetupFarma';
import { SetupIPACategoryLevels } from '../SetupIPACategoryLevels';
import { SetupIPADadosDeConcorrencia } from '../SetupIPADadosDeConcorrencia';
import { SetupIpaBasicConfig } from '../SetupIpaBasicConfig';
import { SetupIpaChannels } from '../SetupIpaChannels';
import { SetupIpaPricingResponsibilities } from '../SetupIpaPricingResponsibilities';
import { SetupIpaProductsSegmentation } from '../SetupIpaProductsSegmentation/SetupIpaProductsSegmentation';
import { SetupIpaStoresAndClusters } from '../SetupIpaStoresAndClusters/SetupIpaStoresAndClusters';
import { SetupSupplierNegotiationConfig } from '../SetupSupplierNegotiationConfig';
import SetupIpaPriceArchitecture from '../SetupIpaPriceArchitecture';
import { hasPriceArchitectureAccess } from './menuVisibility';
import styles from './SetupIPA.module.scss';

const PREFIX_URL = '/administrador/produtos/IPA';

type ContractAdditionalType = string;

type MenuItemVisibilityContext = {
  user: ReturnType<typeof selectorUser>;
  contratosAdicionais: ContractAdditionalType[];
};

type MenuItem = {
  to: string;
  label: string;
  component: React.ComponentType;
  isVisible?: (context: MenuItemVisibilityContext) => boolean;
};

const DEFAULT_MENU_ITEM_VISIBILITY = () => true;

const isMenuItemVisible = (item: MenuItem, context: MenuItemVisibilityContext) => {
  return (item.isVisible ?? DEFAULT_MENU_ITEM_VISIBILITY)(context);
};

const MENU_ITEMS: Record<string, MenuItem> = {
  'configuracoes-basicas': {
    to: 'configuracoes-basicas',
    label: 'Configurações Básicas',
    component: SetupIpaBasicConfig,
  },
  'dados-de-concorrencia': {
    to: 'dados-de-concorrencia',
    label: 'Dados de concorrência',
    component: SetupIPADadosDeConcorrencia,
  },
  'negociacao-com-fornecedores': {
    to: 'negociacao-com-fornecedores',
    label: 'Negociação com fornecedores',
    component: SetupSupplierNegotiationConfig,
    isVisible: ({ contratosAdicionais }) => contratosAdicionais.includes('MODULO_NEGOCIACAO'),
  },
  // Desabilitado temporariamente
  // A api não está funcionando

  // 'dados-do-infopanel': {
  //     to: 'dados-do-infopanel',
  //     label: 'Dados do InfoPanel',
  //     component: SetupIPAForm,
  // },
  'segmentacao-de-produtos': {
    to: 'segmentacao-de-produtos',
    label: 'Segmentação de Produtos',
    component: SetupIpaProductsSegmentation,
  },
  'niveis-de-categoria': {
    to: 'niveis-de-categoria',
    label: 'Níveis de Categoria',
    component: SetupIPACategoryLevels,
  },
  'responsaveis-pela-precificacao': {
    to: 'responsaveis-pela-precificacao',
    label: 'Responsáveis pela precificação',
    component: SetupIpaPricingResponsibilities,
  },
  'lojas-e-clusters': {
    to: 'lojas-e-clusters',
    label: 'Lojas e Clusters',
    component: SetupIpaStoresAndClusters,
  },
  'canais-de-venda': {
    to: 'canais-de-venda',
    label: 'Canais de Venda',
    component: SetupIpaChannels,
  },
  farma: {
    to: 'farma',
    label: 'Farma',
    component: SetupFarma,
  },
  campanhas: {
    to: 'campanhas',
    label: 'Campanhas promocionais',
    component: SetupCampaign,
    isVisible: ({ contratosAdicionais }) => contratosAdicionais.includes('CAMPANHAS_PROMOCIONAIS'),
  },
  'arquitetura-de-precos': {
    to: 'arquitetura-de-precos',
    label: 'Arquitetura de preços',
    component: SetupIpaPriceArchitecture,
    isVisible: ({ contratosAdicionais }) => hasPriceArchitectureAccess(contratosAdicionais),
  },
};

const { to: DEFAULT_PATH } = MENU_ITEMS['dados-de-concorrencia'];

type SetupIPAProps = RouteComponentProps<Record<string, string>> &
  React.HtmlHTMLAttributes<HTMLDivElement> & {
    usuario?: {
      tipoAdicionalContratos?: {
        IPA?: string[];
      };
    };
  };

const SetupIPA = ({ className, match, ...props }: SetupIPAProps) => {
  const user = useSelector(selectorUser);
  const params = match.params?.[0];
  const isUserLoaded = user != null;
  const contratosAdicionais = useMemo(() => user?.tipoAdicionalContratos?.IPA ?? [], [user?.tipoAdicionalContratos?.IPA]);
  const visibilityContext = useMemo(
    () => ({
      user,
      contratosAdicionais,
    }),
    [contratosAdicionais, user],
  );

  const activePath = (params || DEFAULT_PATH) as keyof typeof MENU_ITEMS;

  const [selectedMenu, setSelectedMenu] = useState(activePath);

  const availableMenuItems = useMemo(() => Object.entries(MENU_ITEMS).filter(([_, item]) => isMenuItemVisible(item, visibilityContext)), [visibilityContext]);

  const fallbackPath = (availableMenuItems[0]?.[1].to ?? DEFAULT_PATH) as keyof typeof MENU_ITEMS;
  const requestedMenuItem = availableMenuItems.find(([key]) => key === activePath)?.[1];
  const hasRequestedMenuAccess = requestedMenuItem != null;
  const currentMenuItem = availableMenuItems.find(([key]) => key === selectedMenu)?.[1];

  useEffect(() => {
    if (!isUserLoaded && !hasRequestedMenuAccess) return;

    setSelectedMenu(hasRequestedMenuAccess ? activePath : fallbackPath);
  }, [activePath, fallbackPath, hasRequestedMenuAccess, isUserLoaded]);

  const MemoMenuItem = useMemo(() => {
    return currentMenuItem?.component ?? MENU_ITEMS[fallbackPath].component;
  }, [currentMenuItem, fallbackPath]);

  if (!params) {
    History.push(`${PREFIX_URL}/${DEFAULT_PATH}`);
    return null;
  }

  if (!isUserLoaded && !hasRequestedMenuAccess) {
    return null;
  }

  if (isUserLoaded && !hasRequestedMenuAccess) {
    History.push(`${PREFIX_URL}/${fallbackPath}`);
    return null;
  }

  if (!currentMenuItem) {
    return null;
  }

  return (
    <div className={classNames(styles['setup-ipa'], className)} {...props}>
      <SetupMenu className={styles['setup-ipa__setup-menu']}>
        {availableMenuItems.map(([key, { to, label }]) => (
          <SetupMenuItem key={key} to={`${PREFIX_URL}/${to}`}>
            {label}
          </SetupMenuItem>
        ))}
      </SetupMenu>

      <div>
        <MemoMenuItem />
        <LoadingSpinerArea area="setup-ipa" height="100%" size="sm" />
      </div>
    </div>
  );
};

export default withRouter(SetupIPA);
